import React, { useState, useEffect } from 'react'
import styled from 'particles/themes/styled'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks/index'
import { DragDropContext, Droppable, Draggable, DropResult, DraggingStyle, NotDraggingStyle } from 'react-beautiful-dnd'
import {
    useTable,
    Column,
    useRowSelect,
    UseRowSelectInstanceProps,
    UseRowSelectRowProps,
    useMountedLayoutEffect,
    PluginHook,
} from 'react-table'
import { theme } from 'theme'
import Icon from 'components/Icon/Icon'
import { RowProps } from '..'
import PriorityListHeader from './PriorityListHeaderBar'
import { Paper } from '@mui/material'
import { massRegistration, savePriorityListOrdering } from 'state/registrations'
import {
    selectSelectedCampaignUuid,
    setPriorityListUuids,
    MASS_REGISTRATION_ACTION_NAME_ENUM,
    selectFilteringParams,
    MAX_PAGE_SIZE,
    selectActivePageNum,
} from 'state/review'
import { selectPriorityListRows } from 'state'
import ReviewEmptyState from 'components/Submissions/Review/EmptyState'
import ConfirmModal from './ConfirmModal'
import { GLOBAL_MODAL_TYPE, openModal } from 'state/constants'
import Checkbox from '@mui/material/Checkbox'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { REGISTRATION_STATUS_ENUM } from 'services/api/registrations'

interface DraggableTableProps {
    columns: Column<RowProps>[]
    hideToolbar: boolean
    dragingHeaderColor?: string
    dragingRowColor?: string
    className?: string
}

const Styles = styled.div`
    table {
        width: 100%;
        max-height: calc(100vh - 187px);
        border-spacing: 0;
        border-radius: 4px;
        border: 1px solid ${theme.newColors.neutral200};
        border-top-color: ${theme.newColors.neutral200};
        border-top-style: solid;
        border-top-width: 1px;
        border-right-color: ${theme.newColors.neutral200};
        border-right-style: solid;
        border-right-width: 1px;
        border-bottom-color: ${theme.newColors.neutral200};
        border-bottom-style: solid;
        border-bottom-width: 1px;
        border-left-color: ${theme.newColors.neutral200};

        tr {
            height: 46px;
            th:first-child,
            td:first-child {
                padding-left: ${theme.newSpacing.xs};
            }
            :last-child {
                td {
                    border-bottom: 0;
                }
            }
        }
        th {
            position: relative;

            &:after {
                position: absolute;
                height: 100%;
                left: 0;
                top: 0;
                content: '';
                width: 1px;
                background-color: white;
            }

            &:first-child:after {
                display: none;
            }
        }

        th,
        td {
            margin: 0;
            padding: 0 16px;
            text-align: left;
            &:first-child {
                padding: 0px;
            }
            :last-child {
                border-right: 0;
            }
        }
        td {
            border-left: 1px solid ${theme.newColors.neutral100};
            padding: 0 16px;
        }
    }
`

interface InputProps {
    indeterminate?: boolean
    name?: string
}

const useCombinedRefs = (...refs: any[]): React.MutableRefObject<any> => {
    const targetRef = React.useRef()

    React.useEffect(() => {
        refs.forEach(ref => {
            if (!ref) return

            if (typeof ref === 'function') {
                ref(targetRef.current)
            } else {
                ref.current = targetRef.current
            }
        })
    }, [refs])

    return targetRef
}

// fix typescript ref type issue: https://github.com/TanStack/react-table/discussions/1989
const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const { indeterminate, ...rest } = props
    const defaultRef = React.useRef()
    const resolvedRef = useCombinedRefs(ref, defaultRef)

    React.useEffect(() => {
        resolvedRef.current.indeterminate = indeterminate
    }, [resolvedRef, indeterminate])

    return (
        <>
            <Checkbox ref={resolvedRef} indeterminate={indeterminate} {...rest} />
        </>
    )
})
IndeterminateCheckbox.displayName = 'CheckBox'

type TableProps<T extends object> = {
    data: T[]
    columns: Column<T>[]
    onDragEnd: any
    dragingHeaderColor: string
    dragingRowColor: string
    disableDragging?: boolean
    setSelectedRows: any
    showMultiSelect?: boolean
}
const CheckBoxHeaderContainer = styled.div`
    width: 62px;
    padding-left: 14px;
`
const CheckBoxCellContainer = styled.div`
    display: flex;
    align-items: center;
    width: 62px;
    .drag-icon {
        margin-left: 4px;
    }
    span {
        padding: 0px;
    }
`

const Table: React.FC<TableProps<RowProps>> = ({
    columns,
    data,
    onDragEnd,
    dragingHeaderColor,
    dragingRowColor,
    disableDragging = false,
    setSelectedRows,
    showMultiSelect,
}): JSX.Element => {
    const getListStyle = (isDraggingOver: boolean): object => ({
        background: isDraggingOver ? dragingHeaderColor : theme.colors.brightGray,
    })

    const getItemStyle = (
        isDragging: boolean,
        draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
    ): object => ({
        userSelect: 'none',
        background: isDragging ? dragingRowColor : theme.newColors.neutral50,
        ...draggableStyle,
    })
    const tablePluginHooks: PluginHook<RowProps>[] = showMultiSelect
        ? [
              useRowSelect,
              hooks => {
                  hooks.visibleColumns.push(columns => [
                      {
                          id: 'selection',
                          Header: function getHeader({
                              getToggleAllRowsSelectedProps,
                          }: UseRowSelectInstanceProps<any>): JSX.Element {
                              return (
                                  <CheckBoxHeaderContainer>
                                      <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                                  </CheckBoxHeaderContainer>
                              )
                          },
                          Cell: function getCell({ row }: { row: UseRowSelectRowProps<any> }): JSX.Element {
                              return (
                                  <CheckBoxCellContainer>
                                      <div className="drag-icon">
                                          <Icon.Drag />
                                      </div>
                                      <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                                  </CheckBoxCellContainer>
                              )
                          },
                          width: 53,
                          maxWidth: 53,
                          minWidth: 53,
                          height: '46px',
                      },
                      ...columns,
                  ])
              },
          ]
        : []
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, selectedFlatRows } = useTable<RowProps>(
        {
            columns,
            data,
        },
        ...tablePluginHooks,
    )

    useMountedLayoutEffect(() => {
        setSelectedRows(selectedFlatRows.map(row => row.original))
    }, [setSelectedRows, selectedFlatRows])
    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={'tableRow'}>
                {(provided, snapshot): JSX.Element => (
                    <table {...getTableProps()} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                        <thead>
                            {headerGroups.map((headerGroup, headerGroupIndex) => (
                                <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                                    {headerGroup.headers.map(column => (
                                        <th
                                            {...column.getHeaderProps({
                                                style: {
                                                    minWidth: column.minWidth,
                                                    width: column.width,
                                                    maxWidth: column.maxWidth,
                                                    fontWeight: 'normal',
                                                    borderBottomColor: theme.newColors.neutral200,
                                                    borderBottomStyle: 'solid',
                                                    borderBottomWidth: '1px',
                                                    height: '55px',
                                                    fontSize: '14px',
                                                    color: theme.newColors.neutral50,
                                                },
                                            })}
                                            key={column.id}
                                        >
                                            {column.render('Header')}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {rows.map((row, index) => {
                                prepareRow(row)
                                return (
                                    <Draggable
                                        key={row.id}
                                        draggableId={row.id}
                                        index={index}
                                        isDragDisabled={disableDragging || rows.length === 1}
                                    >
                                        {
                                            // provided type needs to be any, if not will have react-beautiful-dnd type check error: https://stackoverflow.com/questions/62557063/react-beautiful-dnd-typescript-compiler-issues
                                            (provided: any, snapshot): JSX.Element => (
                                                <tr
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={{
                                                        ...getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style,
                                                        ),
                                                        ...{ height: '46px' },
                                                    }}
                                                    {...row.getRowProps()}
                                                    key={row.id}
                                                >
                                                    {row.cells.map((cell, index) => {
                                                        return (
                                                            <td
                                                                {...cell.getCellProps({
                                                                    style: {
                                                                        minWidth: cell.column.minWidth,
                                                                        width: cell.column.width,
                                                                        maxWidth: cell.column.maxWidth,
                                                                        height: '75px',
                                                                        paddingBlock: 8,
                                                                        borderBottom:
                                                                            '1px solid rgba(224, 224, 224, 1)',
                                                                        fontSize: theme.newFontSizes.m,
                                                                        fontWeight: theme.fontWeight.medium,
                                                                        lineHeight: theme.lineHeight.l,
                                                                    },
                                                                })}
                                                                key={index}
                                                            >
                                                                {cell.render('Cell')}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                            )
                                        }
                                    </Draggable>
                                )
                            })}
                            {provided.placeholder}
                        </tbody>
                    </table>
                )}
            </Droppable>
        </DragDropContext>
    )
}

const DraggableTable: React.FC<DraggableTableProps> = ({
    columns,
    hideToolbar,
    dragingHeaderColor = theme.newColors.neutral100,
    dragingRowColor = theme.colors.hawkesBlue,
    className,
}) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const filteringParams = useSelector(selectFilteringParams)
    const priorityListRows = useSelector(selectPriorityListRows)
    const [selectedRows, setSelectedRows] = useState<RowProps[]>([])
    const [disableSaveChanges, setDisableSaveChanges] = useState<boolean>(true)
    const [rows, setRows] = useState<RowProps[]>([])
    const activePageNum = useSelector(selectActivePageNum)
    const canMoveRegistrationToPending = usePermission(CampaignSubmission.MoveRegistrationToPendingApproval)
    const canApproveRegistrationAndManageWaitlist = usePermission(
        CampaignSubmission.ApproveRegistrationAndManageWaitList,
    )

    useEffect(() => {
        if (priorityListRows) {
            setRows(priorityListRows)
        }
    }, [priorityListRows])

    const moveBackToWaitlist = (selectedUuids: string[]): void => {
        if (campaignUuid) {
            dispatch(
                massRegistration({
                    campaignUuid: campaignUuid,
                    uuids: selectedUuids,
                    action_name: MASS_REGISTRATION_ACTION_NAME_ENUM.REMOVE_PRIORITY,
                    getRegistrationListPayload: {
                        status: REGISTRATION_STATUS_ENUM.WAITLISTED,
                        filteringParams,
                        offset: activePageNum * MAX_PAGE_SIZE,
                    },
                }),
            )
        }
    }

    const handleClickMoveBackToWaitlist = (selectedRows: RowProps[]): void => {
        const selectedUuids = selectedRows.map(row => row.uuid)
        if (selectedRows.length > 0 && selectedRows.length == rows.length) {
            dispatch(setPriorityListUuids(selectedUuids))
            dispatch(openModal(GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_BACK_TO_WAITLIST))
        } else {
            moveBackToWaitlist(selectedUuids)
        }
    }

    const handleMoveBackToPending = (selectedRows: RowProps[]): void => {
        const selectedUuids = selectedRows.map(row => row.uuid)
        if (campaignUuid) {
            dispatch(
                massRegistration({
                    campaignUuid: campaignUuid,
                    uuids: selectedUuids,
                    action_name: MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_WAITLISTED_TO_PENDING,
                    dataType: 'priorityList',
                    getRegistrationListPayload: {
                        filteringParams,
                        status: REGISTRATION_STATUS_ENUM.WAITLISTED,
                        is_priority: true,
                    },
                }),
            )
        }
    }

    const handleClickSaveChanges = (): void => {
        if (campaignUuid) {
            const sortingPriorityList = rows.map(item => item.uuid)
            dispatch(
                savePriorityListOrdering({
                    campaignUuid,
                    sortingPriorityList,
                }),
            )
        }
    }
    const reorderValues = (startIndex: number, endIndex: number): RowProps[] => {
        const newValues = Object.assign([], rows)
        const [removed] = newValues.splice(startIndex, 1)
        newValues.splice(endIndex, 0, removed)
        return newValues
    }

    const onDragEnd = (result: DropResult): void => {
        const { destination, source } = result
        // dropped outside the list
        if (!destination) {
            return
        }
        if (destination.droppableId === source.droppableId && destination.index === source.index) {
            return
        }

        const reorderedValues = reorderValues(source.index, destination.index)

        setRows(reorderedValues)
        setDisableSaveChanges(false)
    }
    return (
        <>
            <PriorityListHeader
                onClickMoveBackToWaitlist={
                    canApproveRegistrationAndManageWaitlist
                        ? (): void => handleClickMoveBackToWaitlist(selectedRows)
                        : undefined
                }
                onClickMoveBackToPending={
                    canMoveRegistrationToPending ? (): void => handleMoveBackToPending(selectedRows) : undefined
                }
                onClickSaveChanges={canApproveRegistrationAndManageWaitlist ? handleClickSaveChanges : undefined}
                disableSaveChanges={disableSaveChanges}
                disableMoveBackToPending={selectedRows.length <= 0}
                disableMoveBackToWaitlist={selectedRows.length <= 0}
                hideToolbar={hideToolbar}
            />
            <Paper
                style={{ height: '979px', width: '100%', maxWidth: '100%', overflowX: 'scroll' }}
                className={className}
            >
                <Styles>
                    {canApproveRegistrationAndManageWaitlist || canMoveRegistrationToPending ? (
                        <Table
                            columns={columns}
                            data={rows}
                            onDragEnd={onDragEnd}
                            dragingHeaderColor={dragingHeaderColor}
                            dragingRowColor={dragingRowColor}
                            disableDragging={!canApproveRegistrationAndManageWaitlist}
                            setSelectedRows={setSelectedRows}
                            showMultiSelect={true}
                        />
                    ) : (
                        <Table
                            columns={columns}
                            data={rows}
                            onDragEnd={onDragEnd}
                            dragingHeaderColor={dragingHeaderColor}
                            dragingRowColor={dragingRowColor}
                            setSelectedRows={setSelectedRows}
                            showMultiSelect={false}
                        />
                    )}
                </Styles>
                {rows.length <= 0 && (
                    <div>
                        <ReviewEmptyState
                            my={'0px'}
                            title={newT('emptyState.noRegistrationTitle')}
                            body={newT('emptyState.addPartipostersToPriorityListSubtitle')}
                        />
                    </div>
                )}
            </Paper>
            <ConfirmModal onConfirm={moveBackToWaitlist} />
        </>
    )
}

export default styled(DraggableTable)`
    .empty-state {
        width: 393.55px;
        margin-left: auto;
        margin-right: auto;
        transform: scale(0.85);
    }
`
